import * as queryString from 'query-string';
import piexif from 'piexifjs';
import lodashGet from 'lodash/get';
import lodashSetWith from 'lodash/setWith';

// NOTE: If an image is cached in the browser without having set the Origin
// request header (e.g. via an img tag without crossOrigin="Anonymous"), then
// adding the image to a Canvas will give a CORS error.  So we bust the cache.
// (For some reason the "crossOrigin" tag in Sticker.js doesn't work on Safari)
const bustCache = (url, cacheBuster = { CORS: true }) => {
  const { url: base, query: oldQuery } = queryString.parseUrl(url);
  const newQuery = queryString.stringify(
    { ...oldQuery, ...cacheBuster },
    { sort: false },
  );
  return [base, newQuery].filter((x) => !!x).join('?');
};

// Wrapper to use Image load as a Promise
const _ImageLoad = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      resolve(image);
    };

    image.onerror = () => {
      reject(new Error('Error loading photo!'));
    };

    image.crossOrigin = 'Anonymous';
    image.src = src;
  });

// Wrapper to use FileReader load as a Promise
const _FileReaderLoad = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result);
    };

    reader.onerror = () => {
      reject(new Error('Error reading file!'));
    };

    reader.readAsDataURL(blob);
  });

// Returns { imgData, dataURL }
// Either property can be missing, depending on input options
// (We group these together so that the same "cache-busted" URL is used for both
//  "fetch" and "image.src", so that the uncached image is only downloaded once)
const loadImageFromSrc = async ({
  src,
  returnImageData = false,
  returnDataURL = false,
}) => {
  const srcNoCache = bustCache(src);
  const result = {};

  if (returnDataURL) {
    try {
      const fetchResult = await fetch(srcNoCache);
      const blob = await fetchResult.blob();
      result.dataURL = await _FileReaderLoad(blob);
    } catch (e) {}
  }

  if (returnImageData) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const img = await _ImageLoad(srcNoCache);

    const w = img.width;
    const h = img.height;

    canvas.width = w;
    canvas.height = h;

    ctx.drawImage(img, 0, 0, w, h, 0, 0, w, h);

    result.imgData = ctx.getImageData(0, 0, w, h);
  }

  return result;
};

const getDataURLFromUint8Array = ({ data, width, height, jpgQuality = 0.8 }) =>
  new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = width;
    canvas.height = height;

    const imageData = ctx.createImageData(width, height);
    imageData.data.set(data);
    ctx.putImageData(imageData, 0, 0);

    const result = canvas.toDataURL('image/jpeg', jpgQuality);
    resolve(result);
  });

const piexifCopyExif = ({ exifOriginal, width, height }) => {
  // Copy some EXIF data over
  const exifFinal = {};
  [
    'GPS',
    `0th[${piexif.ImageIFD.XResolution}]`,
    `0th[${piexif.ImageIFD.YResolution}]`,
    `0th[${piexif.ImageIFD.ResolutionUnit}]`,
    `0th[${piexif.ImageIFD.DateTime}]`,
    `0th[${piexif.ImageIFD.ImageDescription}]`,
    `0th[${piexif.ImageIFD.Make}]`,
    `0th[${piexif.ImageIFD.Model}]`,
    `0th[${piexif.ImageIFD.Artist}]`,
    `Exif[${piexif.ExifIFD.ExifVersion}]`,
    `Exif[${piexif.ExifIFD.DateTimeOriginal}]`,
    `Exif[${piexif.ExifIFD.DateTimeDigitized}]`,
    `Exif[${piexif.ExifIFD.SubSecTime}]`,
    `Exif[${piexif.ExifIFD.SubSecTimeOriginal}]`,
    `Exif[${piexif.ExifIFD.SubSecTimeDigitized}]`,
    `Exif[${piexif.ExifIFD.SubjectArea}]`,
    `Exif[${piexif.ExifIFD.SubjectDistance}]`,
    `Exif[${piexif.ExifIFD.SubjectDistanceRange}]`,
    `Exif[${piexif.ExifIFD.SubjectLocation}]`,
    `Exif[${piexif.ExifIFD.UserComment}]`,
  ].forEach((path) => {
    const val = lodashGet(exifOriginal, path, null);
    if (val !== null && val !== undefined) {
      lodashSetWith(exifFinal, path, val, Object);
    }
  });

  // Write size and orientation
  [
    [`0th[${piexif.ImageIFD.Orientation}]`, 1], // "1" means no rotation
    [`0th[${piexif.ImageIFD.ImageWidth}]`, width],
    [`0th[${piexif.ImageIFD.ImageLength}]`, height],
  ].forEach(([path, value]) => {
    lodashSetWith(exifFinal, path, value, Object);
  });

  return exifFinal;
};

export { loadImageFromSrc, getDataURLFromUint8Array, piexifCopyExif };
